import { useState, useEffect } from "react";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import * as THREE from "three";
import getModelType from "./getModelType";
import JSZip from "jszip";

const useFetchAndSetModelUrl = (ipfsLink) => {
  const [model, setModel] = useState(null);
  const [modelType, setModelType] = useState(null);
  const [textureUrls, setTextureUrls] = useState([]);

  useEffect(() => {
    const fetchModelData = async () => {
      try {
        const response = await fetch(ipfsLink);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const detectedType = getModelType(uint8Array);

        switch (detectedType) {
          case "glb":
            {
              const objURL = URL.createObjectURL(new Blob([arrayBuffer]));
              setModel(objURL);
              setModelType("glb");
              setTextureUrls([]);
            }
            break;
          case "fbx":
            {
              const blob = new Blob([arrayBuffer]);
              const objectURL = URL.createObjectURL(blob);
              setModel(objectURL);
              setModelType("fbx");
              setTextureUrls([]);
            }
            break;
          case "obj":
            {
              const text = new TextDecoder().decode(uint8Array);
              const loader = new OBJLoader();
              const model = loader.parse(text);
              setModel(model);
              setModelType("obj");
              setTextureUrls([]);
            }
            break;
          case "stl":
            {
              const material = new THREE.MeshStandardMaterial({
                color: 0x999999,
              });
              const mesh = new THREE.Mesh(
                new STLLoader().parse(arrayBuffer),
                material
              );
              setModel(mesh);
              setModelType("stl");
              setTextureUrls([]);
            }
            break;
          case "zip":
            {
              const zip = await JSZip.loadAsync(arrayBuffer);
              const files = Object.keys(zip.files);

              for (const name of files) {
                const lower = name.toLowerCase();
                if (lower.endsWith(".obj")) {
                  const arrayBuffer = await zip.files[name].async(
                    "arraybuffer"
                  );
                  const uint8Array = new Uint8Array(arrayBuffer);
                  const text = new TextDecoder().decode(uint8Array);
                  const loader = new OBJLoader();
                  const model = loader.parse(text);
                  setModel(model);
                  setModelType("obj");
                  setTextureUrls([]);
                  break;
                } else if (lower.endsWith(".fbx")) {
                  const arrayBuffer = await zip.files[name].async(
                    "arraybuffer"
                  );
                  const blob = new Blob([arrayBuffer]);
                  const objectURL = URL.createObjectURL(blob);
                  setModel(objectURL);
                  setModelType("fbx");
                  setTextureUrls([]);
                  break;
                }
              }
            }
            break;
          default:
            throw new Error("Unsupported or unknown file type.");
        }
      } catch (error) {
        console.error(
          "Error fetching and setting model and texture URLs:",
          error
        );
      }
    };

    if (ipfsLink) {
      fetchModelData();
    }
  }, [ipfsLink]);

  return { model, modelType, textureUrls };
};

export default useFetchAndSetModelUrl;
